import gql from 'graphql-tag'
import React, { useEffect, useRef } from 'react'
import { Query } from 'react-apollo'
import { useCurrentUserProfile } from '../current-user/api/use-current-user-profile'
import { CurrentUserProfile } from '../current-user/api/requests/get-current-user-profile'
import { useCurrentOrganization } from 'contrastBase/state'

interface ChildProps {
  accessToken: string
  clientHost: string
  remoteEntry: string
  expiresAt: number
  role: string
  organizationID: string
  currentUserProfile: CurrentUserProfile
}

export interface ServerlessAuthProps {
  children: (props: ChildProps) => React.ReactNode
}

export const serverlessAccessTokenQuery = gql`
  query ServerlessAccessToken($organizationID: String!) {
    serverlessAccessToken(organizationID: $organizationID) {
      accessToken
      expiresAt
      host
      remoteEntry
    }
  }
`

interface ServerlessAccessTokenVars {
  readonly organizationID: string
}

interface ServerlessAccessTokenData {
  serverlessAccessToken: {
    accessToken: string
    expiresAt: number
    host: string
    remoteEntry: string
  }
}

interface QueryRef {
  refetch: () => void
  startPolling: (pollInterval: number) => void
  stopPolling: () => void
}

export interface ServerlessQueryEvent {
  refetch?: boolean
  pollInterval?: number
}

function useServerlessQueryEvent() {
  const queryRef = useRef<QueryRef>()

  useEffect(() => {
    const queryCustomEvent = (e: CustomEvent) => {
      const { refetch, pollInterval } = e.detail as ServerlessQueryEvent

      if (queryRef.current) {
        if (refetch) {
          queryRef.current.refetch()
        }

        if (pollInterval && pollInterval > 0) {
          queryRef.current.startPolling(pollInterval)
        }

        if (pollInterval === 0) {
          queryRef.current.stopPolling()
        }
      }
    }

    document.addEventListener('ServerlessQueryEvent', queryCustomEvent)

    return () =>
      document.removeEventListener('ServerlessQueryEvent', queryCustomEvent)
  }, [])

  return queryRef
}

export function ServerlessAuth({ children }: ServerlessAuthProps) {
  let errorCount = 0

  const { currentOrganization } = useCurrentOrganization()
  const organizationID = currentOrganization?.organizationUuid ?? ''
  const { currentUserProfile } = useCurrentUserProfile({
    organizationUuid: organizationID,
    enabled: !!organizationID
  })

  const queryRef = useServerlessQueryEvent()

  return organizationID && currentUserProfile ? (
    <Query<ServerlessAccessTokenData, ServerlessAccessTokenVars>
      query={serverlessAccessTokenQuery}
      variables={{ organizationID }}
      pollInterval={15 * 60 * 1000}
    >
      {({ loading, data, error, startPolling, stopPolling, refetch }) => {
        // store Query functions to use them in CustomEvent from micro-frontend
        queryRef.current = {
          refetch,
          stopPolling,
          startPolling
        }

        if (loading) {
          return false
        }

        if (errorCount >= 3) {
          stopPolling()
          return false
        }

        // onError retry to refresh token every 3 sec until it success
        if (error || !data || !data.serverlessAccessToken) {
          errorCount++
          startPolling(3 * 1000)
          return false
        }

        errorCount = 0
        const {
          accessToken,
          host: clientHost,
          remoteEntry,
          expiresAt
        } = data.serverlessAccessToken

        // schedule next token when 90% time over
        const nextSchedule = Math.ceil((expiresAt * 1000 - Date.now()) * 0.9)
        startPolling(nextSchedule)

        return children({
          accessToken,
          clientHost,
          remoteEntry,
          expiresAt,
          role: '',
          organizationID,
          currentUserProfile
        })
      }}
    </Query>
  ) : null
}
